import { DateTime } from "luxon";
import React, { useEffect, useRef, useState } from "react";
import { AiFillSliders } from "react-icons/ai";

const DRAG_HANDLE = {
  LEFT: "left",
  RIGHT: "right",
  MOVE: "move",
} as const;

type DragHandleType = (typeof DRAG_HANDLE)[keyof typeof DRAG_HANDLE];

interface RecordingsZoomOverviewTimelineProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  currentPosition?: number;
  timeline: [number, number];
  timezone: string;
  visibleStart: number;
  visibleEnd: number;
  setOffset: React.Dispatch<React.SetStateAction<number>>;
  setScale: React.Dispatch<React.SetStateAction<number>>;
}

const RecordingsZoomOverviewTimeline: React.FC<
  RecordingsZoomOverviewTimelineProps
> = ({
  containerRef,
  currentPosition,
  timeline,
  timezone,
  visibleStart,
  visibleEnd,
  setOffset,
  setScale,
}) => {
  const [overviewDrag, setOverviewDrag] = useState<null | DragHandleType>(null);
  const [zoomDragStart, setZoomDragStart] = useState<number | null>(null);
  const [zoomDragEnd, setZoomDragEnd] = useState<number | null>(null);
  const overviewStartXRef = useRef(0);
  const overviewInitialRangeRef = useRef<[number, number] | null>(null);

  const totalMs = timeline[1] - timeline[0];

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (
        !overviewDrag ||
        !overviewInitialRangeRef.current ||
        !containerRef.current
      )
        return;

      const containerWidth = containerRef.current.offsetWidth - 76;
      const overviewContainerWidth = containerRef.current.offsetWidth - 48;
      const msPerPx = totalMs / overviewContainerWidth;
      const deltaPx = e.clientX - overviewStartXRef.current;
      const deltaMs = deltaPx * msPerPx;

      let [start, end] = overviewInitialRangeRef.current;
      if (overviewDrag === DRAG_HANDLE.LEFT) {
        start = Math.min(
          end - msPerPx * 2,
          Math.max(timeline[0], start + deltaMs),
        );
      } else if (overviewDrag === DRAG_HANDLE.RIGHT) {
        end = Math.max(
          start + msPerPx * 2,
          Math.min(timeline[1], end + deltaMs),
        );
      } else if (overviewDrag === DRAG_HANDLE.MOVE) {
        const duration = end - start;
        start = Math.max(timeline[0], start + deltaMs);
        end = start + duration;
        if (end > timeline[1]) {
          end = timeline[1];
          start = end - duration;
        }
      }

      const newScale = Math.min(2048, totalMs / (end - start));
      const centerMs = (start + end) / 2;
      const newOffset =
        (timeline[0] + totalMs / newScale / 2 - centerMs) *
        (containerWidth / (totalMs / newScale));

      setScale(newScale);
      setOffset(
        Math.max(-(newScale - 1) * containerWidth, Math.min(newOffset, 0)),
      );
    };

    const handleMouseUp = () => {
      setOverviewDrag(null);
      overviewInitialRangeRef.current = null;
    };

    if (overviewDrag) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [overviewDrag, timeline, setScale, setOffset, containerRef, totalMs]);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    const handleMouseMove = (e: MouseEvent) => {
      if (zoomDragStart === null) return;

      const rect = container.getBoundingClientRect();
      const px = e.clientX - rect.left - 48;
      const clampedX = Math.max(0, Math.min(container.offsetWidth - 48, px));
      setZoomDragEnd(clampedX);
    };

    const handleMouseUp = () => {
      if (
        zoomDragStart !== null &&
        zoomDragEnd !== null &&
        Math.abs(zoomDragEnd - zoomDragStart) > 4
      ) {
        const containerWidth = container.offsetWidth - 76;
        const overviewContainerWidth = container.offsetWidth - 48;
        const totalMs = timeline[1] - timeline[0];

        const dragStartPx = Math.min(zoomDragStart, zoomDragEnd);
        const dragEndPx = Math.max(zoomDragStart, zoomDragEnd);
        const msPerPx = totalMs / overviewContainerWidth;

        const start = timeline[0] + dragStartPx * msPerPx;
        const end = timeline[0] + dragEndPx * msPerPx;

        const duration = end - start;
        const newScale = totalMs / duration;
        const centerMs = (start + end) / 2;

        const newOffset =
          (timeline[0] + totalMs / newScale / 2 - centerMs) *
          (containerWidth / (totalMs / newScale));

        setScale(newScale);
        setOffset(
          Math.max(-(newScale - 1) * containerWidth, Math.min(newOffset, 0)),
        );
      }

      setZoomDragStart(null);
      setZoomDragEnd(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    zoomDragStart,
    zoomDragEnd,
    timeline,
    totalMs,
    setScale,
    setOffset,
    containerRef,
  ]);

  const getDayRanges = (timeline: [number, number], timezone: string) => {
    const [startMs, endMs] = timeline;
    const start = DateTime.fromMillis(startMs).setZone(timezone);
    const end = DateTime.fromMillis(endMs).setZone(timezone);

    const ranges: { label: string; start: number; end: number }[] = [];

    let cursor = start.startOf("day");
    if (cursor.toMillis() > startMs) {
      cursor = cursor.minus({ days: 1 });
    }

    while (cursor < end) {
      const rangeStart = Math.max(cursor.toMillis(), startMs);
      const next = cursor.plus({ days: 1 });
      const rangeEnd = Math.min(next.toMillis(), endMs);

      ranges.push({
        label: cursor.toFormat("LLL-dd").toUpperCase(),
        start: rangeStart,
        end: rangeEnd,
      });

      cursor = next;
    }

    return ranges;
  };

  const containerWidth = containerRef.current
    ? containerRef.current.offsetWidth - 76
    : 0;
  const overviewContainerWidth = containerRef.current
    ? containerRef.current.offsetWidth - 48
    : 0;
  const dayRanges = getDayRanges(timeline, timezone);

  return (
    <div className='flex flex-col'>
      <div
        className='relative w-full h-3 bg-zinc-200 cursor-crosshair'
        onMouseDown={(e) => {
          const rect = containerRef.current?.getBoundingClientRect();
          if (!rect) return;
          const px = e.clientX - rect.left - 48;
          setZoomDragStart(px);
          setZoomDragEnd(null);
        }}
        data-testid='overviewTimeline'
      >
        {!!currentPosition && (
          <div
            className='absolute h-full bg-primary-blue-100'
            style={{
              left: 0,
              width: `${((currentPosition - timeline[0]) / totalMs) * 100}%`,
            }}
            data-testid='currentPosition'
          />
        )}

        <div
          className='absolute top-[-16px] z-40 h-[calc(100%_+_16px)] border-x border-slate-400'
          style={{
            left: `${((visibleStart - timeline[0]) / totalMs) * 100}%`,
            width: `${((visibleEnd - visibleStart) / totalMs) * 100}%`,
          }}
        >
          <div
            className='w-full h-full bg-blue-200 opacity-30'
            data-testid='overviewBackground'
          />
          <div
            className='absolute w-[calc(100%_+_2px)] h-1.5 top-[-2px] left-[-1px] cursor-ew-resize bg-slate-200 hover:bg-blue-100 transition-colors'
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              overviewStartXRef.current = e.clientX;
              overviewInitialRangeRef.current = [visibleStart, visibleEnd];
              setOverviewDrag(DRAG_HANDLE.MOVE);
            }}
            data-testid='overviewDrag'
          >
            <AiFillSliders
              size={8}
              className='absolute top-[-1px] left-[calc(50%_-_4px)] text-white'
            />
          </div>
          {/* Resize handles */}
          <div
            className='absolute left-[-2px] top-2 h-4 w-1 bg-transparent cursor-ew-resize'
            onMouseDown={(e) => {
              e.stopPropagation();
              overviewStartXRef.current = e.clientX;
              overviewInitialRangeRef.current = [visibleStart, visibleEnd];
              setOverviewDrag(DRAG_HANDLE.LEFT);
            }}
            data-testid='overviewLeftResize'
          >
            <div className='absolute top-0 left-[-1px] h-full w-[5px] bg-white border border-slate-400' />
          </div>
          <div
            className='absolute right-[-2px] top-2 h-4 w-1 bg-transparent cursor-ew-resize'
            onMouseDown={(e) => {
              e.stopPropagation();
              overviewStartXRef.current = e.clientX;
              overviewInitialRangeRef.current = [visibleStart, visibleEnd];
              setOverviewDrag(DRAG_HANDLE.RIGHT);
            }}
            data-testid='overviewRightResize'
          >
            <div className='absolute top-0 right-[-1px] h-full w-[5px] bg-white border border-slate-400' />
          </div>
        </div>
        {zoomDragStart !== null && zoomDragEnd !== null && (
          <div
            id='overview-visible-box'
            className='absolute top-0 h-full bg-blue-300 opacity-30 border border-blue-500 z-40 pointer-events-none'
            style={{
              left: `${Math.min(zoomDragStart, zoomDragEnd)}px`,
              width: `${Math.abs(zoomDragEnd - zoomDragStart)}px`,
            }}
            data-testid='overviewZoomSelectBox'
          />
        )}
      </div>
      <div
        className='flex w-full border-t border-zinc-300 text-xs text-gray-600 select-none overflow-hidden bg-white'
        data-testid='dayBlocks'
      >
        {dayRanges.map((range, index) => {
          const durationMs = range.end - range.start;
          const width = (durationMs / totalMs) * overviewContainerWidth;

          return (
            <div
              key={range.label}
              onClick={() => {
                const newScale = Math.min(2048, totalMs / durationMs);
                const center = (range.start + range.end) / 2;
                const newOffset =
                  (timeline[0] + totalMs / newScale / 2 - center) *
                  (containerWidth / (totalMs / newScale));

                setScale(newScale);
                setOffset(
                  Math.max(
                    -(newScale - 1) * containerWidth,
                    Math.min(newOffset, 0),
                  ),
                );
              }}
              style={{
                width,
                borderRight:
                  index !== dayRanges.length - 1
                    ? "1px solid #e5e7eb"
                    : undefined,
              }}
              className='text-center cursor-pointer py-1 bg-gray-100 hover:bg-gray-200 transition-colors'
              role='button'
              tabIndex={0}
              title={range.label}
              data-testid={`dayBlock${range.label}`}
            >
              {width > 50 ? range.label : ""}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecordingsZoomOverviewTimeline;
